import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, CheckCircle2, Circle, Download, GraduationCap, FileImage, ChevronRight, Layers, Search, X, ExternalLink } from "lucide-react";
import { renderInline } from "./inline";

const API = process.env.REACT_APP_BACKEND_URL;
const ytIdOf = (src) => (src?.match(/(?:v=|embed\/|youtu\.be\/|shorts\/)([\w-]{6,})/) || [])[1];
const resolve = (u) => (u?.startsWith("/api/") ? `${API}${u}` : u);

const parseBody = (raw) => {
  const blocks = [];
  for (const chunk of (raw || "").split(/\n\s*\n/)) {
    const t = chunk.trim();
    if (!t) continue;
    if (t.startsWith("## ")) blocks.push({ h: t.slice(3).trim() });
    else if (t.split("\n").every((l) => l.trim().startsWith("- ")))
      blocks.push({ ul: t.split("\n").map((l) => l.trim().slice(2)) });
    else blocks.push({ p: t });
  }
  return blocks;
};

function Blocks({ raw }) {
  return parseBody(raw).map((b, i) => {
    if (b.h) return <h3 key={i} className="rp-display text-lg font-medium text-[#262016] mt-6 mb-2">{renderInline(b.h)}</h3>;
    if (b.ul)
      return (
        <ul key={i} className="space-y-2 my-3">
          {b.ul.map((li, j) => (
            <li key={j} className="flex gap-3 text-sm leading-relaxed text-[#5C5042]">
              <span className="shrink-0 mt-[8px] w-1.5 h-1.5 rounded-full" style={{ background: "#B5793F" }} />
              <span style={{ textWrap: "pretty" }}>{renderInline(li)}</span>
            </li>
          ))}
        </ul>
      );
    return <p key={i} className="text-sm leading-relaxed text-[#5C5042] my-3" style={{ textWrap: "pretty" }}>{renderInline(b.p)}</p>;
  });
}

function ProgressBar({ value }) {
  return (
    <div className="h-1.5 rounded-full bg-[#26201614] overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, background: "#B5793F" }} />
    </div>
  );
}

function ModuleCard({ c, onOpen }) {
  const pct = c.lesson_count ? Math.round((c.completed_count / c.lesson_count) * 100) : 0;
  return (
    <button onClick={() => onOpen(c.id)} data-testid={`lms-module-${c.id}`}
            className="rp-foam bg-white/55 text-left overflow-hidden transition-transform hover:-translate-y-0.5 flex flex-col">
      {c.cover && <img src={resolve(c.cover)} alt={c.title} className="w-full h-36 object-cover" />}
      <div className="p-5 flex-1 flex flex-col">
        {c.tag && <span className="text-[11px] uppercase tracking-[0.16em] text-[#B5793F] mb-1">{c.tag}</span>}
        <span className="rp-display block text-base md:text-lg font-medium text-[#262016] leading-snug">{c.title}</span>
        {c.description && <span className="block text-sm text-[#5C5042] mt-1 flex-1 line-clamp-2">{c.description}</span>}
        <span className="block mt-4">
          <ProgressBar value={pct} />
          <span className="flex justify-between mt-2 text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B]">
            <span>{c.submodule_count || 0} sections · {c.completed_count}/{c.lesson_count}</span>
            <span className="font-semibold text-[#B5793F]">{pct === 100 ? "Completed" : pct > 0 ? "Continue" : "Start"}</span>
          </span>
        </span>
      </div>
    </button>
  );
}

/* ---- Flatten nested sub-module tree into a list with depth ---- */
function flattenSubs(subs, depth) {
  const out = [];
  for (const s of subs) {
    out.push({ ...s, _depth: depth });
    if (s.children && s.children.length > 0) {
      out.push(...flattenSubs(s.children, depth + 1));
    }
  }
  return out;
}

/* ---- sidebar sub-module navigation (iterative, not recursive JSX) ---- */
function SubModuleList({ submodules, completed, current, onSelect, currentSubId }) {
  const flat = React.useMemo(() => flattenSubs(submodules, 0), [submodules]);
  const [openIds, setOpenIds] = useState(() => {
    const ids = new Set();
    if (currentSubId) {
      const findPath = (subs, target, path) => {
        for (const s of subs) {
          if (s.id === target) return [...path, s.id];
          if (s.children && s.children.length > 0) {
            const found = findPath(s.children, target, [...path, s.id]);
            if (found) return found;
          }
        }
        return null;
      };
      const p = findPath(submodules, currentSubId, []);
      if (p) p.forEach((id) => ids.add(id));
    }
    return ids;
  });

  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /* Determine visible items: a sub is visible if all its ancestors are open */
  const visible = React.useMemo(() => {
    const result = [];
    const parentStack = []; // stack of {id, depth}
    for (const s of flat) {
      // Trim stack to current depth
      while (parentStack.length > 0 && parentStack[parentStack.length - 1].depth >= s._depth) {
        parentStack.pop();
      }
      // Check if parent is open (or depth 0)
      const parentOpen = parentStack.length === 0 || openIds.has(parentStack[parentStack.length - 1].id);
      if (parentOpen) result.push(s);
      parentStack.push({ id: s.id, depth: s._depth });
    }
    return result;
  }, [flat, openIds]);

  return (
    <div className="space-y-0.5">
      {visible.map((s) => {
        const allLessons = s.lessons || [];
        const hasChildren = s.children && s.children.length > 0;
        const doneCount = allLessons.filter((l) => completed.has(l.id)).length;
        const allDone = doneCount === allLessons.length && allLessons.length > 0;
        const isOpen = openIds.has(s.id);

        return (
          <div key={s.id} data-testid={`lms-sub-${s.id}`} style={{ paddingLeft: s._depth * 16 }}>
            <button onClick={() => toggle(s.id)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors hover:bg-[#26201608]"
                    style={{ color: allDone ? "#B5793F" : "#262016" }}
                    data-testid={`lms-sub-toggle-${s.id}`}>
              <ChevronRight size={14} className={`shrink-0 text-[#8A7E6B] transition-transform ${isOpen ? "rotate-90" : ""}`} />
              <span className="flex-1 min-w-0 truncate">{s.title}</span>
              {!hasChildren && allLessons.length > 0 && <span className="shrink-0 text-[10px] text-[#8A7E6B] font-normal">{doneCount}/{allLessons.length}</span>}
            </button>
            {isOpen && allLessons.length > 0 && (
              <div className="ml-5 pl-3 border-l border-[#26201614] space-y-0.5 pb-1">
                {allLessons.map((l) => {
                  const active = l.id === current;
                  const done = completed.has(l.id);
                  return (
                    <button key={l.id} onClick={() => onSelect(l)} data-testid={`lms-lesson-${l.id}`}
                            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-[13px] transition-colors"
                            style={{ background: active ? "rgba(38,32,22,0.08)" : "transparent" }}>
                      {done ? <CheckCircle2 size={14} className="shrink-0 text-[#B5793F]" /> : <Circle size={14} className="shrink-0 text-[#8A7E6B]" />}
                      <span className={`flex-1 min-w-0 truncate ${active ? "font-semibold text-[#262016]" : "text-[#5C5042]"}`}>{l.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModuleView({ moduleId, onBack, targetLessonId }) {
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/lms/modules/${moduleId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setData(d);
        const done = new Set(d.progress.completed);
        const allLessons = d.lessons || [];
        // If a specific lesson was targeted, use it
        if (targetLessonId) {
          const target = allLessons.find((l) => l.id === targetLessonId);
          if (target) { setCurrent(target.id); return; }
        }
        // Otherwise resume from last or first incomplete
        const resume = allLessons.find((l) => l.id === d.progress.last_lesson_id)
          || allLessons.find((l) => !done.has(l.id)) || allLessons[0];
        if (resume) setCurrent(resume.id);
      })
      .catch(() => setData({ module: null, submodules: [], lessons: [], progress: { completed: [] } }));
  }, [moduleId, targetLessonId]);

  const post = useCallback((lessonId, action) =>
    fetch(`${API}/api/lms/progress`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ module_id: moduleId, lesson_id: lessonId, action }),
    }).then((r) => (r.ok ? r.json() : null)), [moduleId]);

  if (!data)
    return <p className="rp-display rp-italic text-lg text-[#5C5042]">One moment…</p>;
  if (!data.module)
    return <p className="text-sm text-[#8A7E6B]">Module unavailable. <button onClick={onBack} className="underline">Back</button></p>;

  const completed = new Set(data.progress.completed);
  const allLessons = data.lessons || [];
  const lesson = allLessons.find((l) => l.id === current);
  const pct = allLessons.length ? Math.round((completed.size / allLessons.length) * 100) : 0;
  const videos = lesson?.videos || (lesson?.video ? [lesson.video] : []);
  const submodules = data.submodules || [];

  // find which sub-module contains the current lesson
  const currentSubId = lesson ? (lesson.submodule_id || "") : "";

  const selectLesson = (l) => {
    setCurrent(l.id);
    post(l.id, "visit");
  };

  const toggleComplete = async () => {
    const action = completed.has(lesson.id) ? "uncomplete" : "complete";
    const res = await post(lesson.id, action);
    if (res) setData((d) => ({ ...d, progress: { ...d.progress, completed: res.completed } }));
  };

  return (
    <div data-testid="lms-course-view">
      <button onClick={onBack} data-testid="lms-back-btn"
              className="inline-flex items-center gap-2 mb-6 text-sm font-medium text-[#5C5042] hover:text-[#262016] transition-colors">
        <ArrowLeft size={15} /> All modules
      </button>

      <div className="mb-6">
        {data.module.tag && <p className="text-[11px] uppercase tracking-[0.18em] text-[#B5793F] font-medium mb-1">{data.module.tag}</p>}
        <h2 className="rp-display text-2xl md:text-3xl font-light text-[#262016] mb-3" style={{ textWrap: "balance" }}>{data.module.title}</h2>
        <div className="max-w-sm">
          <ProgressBar value={pct} />
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] mt-1.5" data-testid="lms-progress-label">
            {completed.size} of {allLessons.length} lessons · {submodules.length} sections · {pct}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* main lesson content */}
        <div className="rp-foam bg-white/55 overflow-hidden" data-testid="lms-player">
          {lesson ? (
            <>
              {/* Section-based rendering */}
              {lesson.sections?.length > 0 ? (
                <div>
                  <div className="px-6 md:px-8 pt-6 pb-2">
                    <div className="flex items-start justify-between gap-4">
                      <p className="rp-display text-xl md:text-2xl font-medium text-[#262016]" data-testid="lms-lesson-title">{lesson.title}</p>
                      <button onClick={toggleComplete} data-testid="lms-complete-btn"
                              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-colors"
                              style={completed.has(lesson.id)
                                ? { background: "#B5793F", color: "#F6F1E5", border: "1px solid #B5793F" }
                                : { border: "1px solid rgba(38,32,22,0.25)", color: "#262016" }}>
                        <CheckCircle2 size={14} /> {completed.has(lesson.id) ? "Completed" : "Mark complete"}
                      </button>
                    </div>
                  </div>
                  {[...lesson.sections].sort((a, b) => (a.order || 0) - (b.order || 0)).map((sec, si) => {
                    if (sec.type === "video") {
                      const vid = ytIdOf(sec.url);
                      if (!sec.url) return null;
                      return (
                        <div key={sec.id || si} className="w-full bg-black" style={{ aspectRatio: "16/9" }}>
                          {vid ? (
                            <iframe src={`https://www.youtube.com/embed/${vid}?rel=0`} title={`${lesson.title} - Video`} className="w-full h-full"
                                    frameBorder="0" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                          ) : (
                            <video key={`${lesson.id}-${si}`} src={resolve(sec.url)} controls controlsList="nodownload" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="metadata" className="w-full h-full" />
                          )}
                        </div>
                      );
                    }
                    if (sec.type === "text") {
                      return (
                        <div key={sec.id || si} className="px-6 md:px-8 py-4">
                          <Blocks raw={sec.content} />
                        </div>
                      );
                    }
                    if (sec.type === "image") {
                      return (
                        <div key={sec.id || si} className="px-6 md:px-8 py-3">
                          <a href={resolve(sec.url)} target="_blank" rel="noreferrer noopener" className="block rounded-lg overflow-hidden border border-[#26201618] inline-block">
                            <img src={resolve(sec.url)} alt={sec.caption || `Visual ${si + 1}`} className="max-w-full max-h-80 object-contain hover:scale-105 transition-transform" loading="lazy" />
                          </a>
                          {sec.caption && <p className="text-xs text-[#8A7E6B] mt-1 italic">{sec.caption}</p>}
                        </div>
                      );
                    }
                    if (sec.type === "file") {
                      return (
                        <div key={sec.id || si} className="px-6 md:px-8 py-2">
                          <a href={resolve(sec.url)} target="_blank" rel="noreferrer noopener"
                             className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#26201618] text-sm text-[#262016] hover:border-[#B5793F] transition-colors">
                            <Download size={14} className="text-[#B5793F]" /> {sec.filename || "Download"}
                          </a>
                        </div>
                      );
                    }
                    if (sec.type === "link") {
                      return (
                        <div key={sec.id || si} className="px-6 md:px-8 py-2">
                          <a href={sec.url} target="_blank" rel="noreferrer noopener"
                             className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#26201618] text-sm text-[#262016] hover:border-[#B5793F] transition-colors group">
                            <ExternalLink size={14} className="text-[#B5793F] shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium group-hover:text-[#B5793F] transition-colors truncate">{sec.title || sec.url}</p>
                              {sec.description && <p className="text-xs text-[#8A7E6B] mt-0.5 truncate">{sec.description}</p>}
                            </div>
                            <ExternalLink size={12} className="text-[#8A7E6B] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                /* Legacy rendering for lessons without sections */
                <>
                  {videos.length > 0 && (
                    <div className="space-y-5">
                      {videos.filter(v => v).map((v, i) => {
                        const vid = ytIdOf(v);
                        return (
                          <div key={i} className="w-full bg-black" style={{ aspectRatio: "16/9" }}>
                            {vid ? (
                              <iframe src={`https://www.youtube.com/embed/${vid}?rel=0`} title={`${lesson.title} - Video ${i + 1}`} className="w-full h-full"
                                      frameBorder="0" allowFullScreen
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                            ) : (
                              <video key={`${lesson.id}-${i}`} src={resolve(v)} controls controlsList="nodownload" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="metadata" className="w-full h-full" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <p className="rp-display text-xl md:text-2xl font-medium text-[#262016]" data-testid="lms-lesson-title">{lesson.title}</p>
                      <button onClick={toggleComplete} data-testid="lms-complete-btn"
                              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-colors"
                              style={completed.has(lesson.id)
                                ? { background: "#B5793F", color: "#F6F1E5", border: "1px solid #B5793F" }
                                : { border: "1px solid rgba(38,32,22,0.25)", color: "#262016" }}>
                        <CheckCircle2 size={14} /> {completed.has(lesson.id) ? "Completed" : "Mark complete"}
                      </button>
                    </div>
                    {lesson.notes && <Blocks raw={lesson.notes} />}
                    {lesson.images?.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5" data-testid="lms-lesson-images">
                        {lesson.images.map((img, i) => (
                          <a key={i} href={resolve(img)} target="_blank" rel="noreferrer noopener" className="block rounded-lg overflow-hidden border border-[#26201618]">
                            <img src={resolve(img)} alt={`Lesson visual ${i + 1}`} className="w-full h-28 object-cover hover:scale-105 transition-transform" loading="lazy" />
                          </a>
                        ))}
                      </div>
                    )}
                    {lesson.files?.length > 0 && (
                      <div className="mt-6 space-y-3" data-testid="lms-lesson-files">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">Resources</p>
                        {lesson.files.map((f, i) => (
                          <a key={i} href={resolve(f.href)} target="_blank" rel="noreferrer noopener"
                             className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#26201618] text-sm text-[#262016] hover:border-[#B5793F] transition-colors">
                            <Download size={14} className="text-[#B5793F]" /> {f.label || "Download"}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="p-10 text-center">
              <FileImage className="mx-auto mb-3 text-[#B5793F]" size={22} />
              <p className="rp-display rp-italic text-[#8A7E6B]">Lessons for this module are coming soon.</p>
            </div>
          )}
        </div>

        {/* sidebar: sub-module → lesson navigation */}
        <aside className="rp-foam bg-white/45 p-4 h-fit max-h-[80vh] overflow-y-auto" data-testid="lms-lesson-list">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium px-2 mb-3">
            <Layers size={11} className="inline mr-1" /> Sections
          </p>
          {submodules.length > 0 ? (
            <SubModuleList submodules={submodules} completed={completed} current={current}
                          onSelect={selectLesson} currentSubId={currentSubId} />
          ) : (
            /* fallback: flat lesson list (for modules without sub-modules) */
            <div className="space-y-1">
              {allLessons.map((l, i) => {
                const active = l.id === current;
                const done = completed.has(l.id);
                return (
                  <button key={l.id} onClick={() => selectLesson(l)} data-testid={`lms-lesson-${l.id}`}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
                          style={{ background: active ? "rgba(38,32,22,0.08)" : "transparent", color: "#262016" }}>
                    {done ? <CheckCircle2 size={16} className="shrink-0 text-[#B5793F]" /> : <Circle size={16} className="shrink-0 text-[#8A7E6B]" />}
                    <span className={`flex-1 min-w-0 truncate ${active ? "font-semibold" : ""}`}>{i + 1}. {l.title}</span>
                  </button>
                );
              })}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function SearchResults({ results, onSelect }) {
  if (!results || results.length === 0)
    return <p className="text-sm text-[#8A7E6B] py-4 text-center">No matching lessons found.</p>;

  return (
    <div className="space-y-4" data-testid="lms-search-results">
      {results.map((l) => (
        <button key={l.id} onClick={() => onSelect(l)} data-testid={`search-result-${l.id}`}
                className="w-full rp-foam bg-white/55 p-4 text-left transition-transform hover:-translate-y-0.5">
          <p className="text-sm font-medium text-[#262016]">{l.title}</p>
          <p className="text-[11px] text-[#8A7E6B] mt-1">
            {l.module?.title || "Unknown module"} › {l.submodule?.title || ""}
          </p>
          {l.notes && <p className="text-xs text-[#5C5042] mt-1.5 line-clamp-2">{l.notes.replace(/[#\-*]/g, "").slice(0, 150)}</p>}
        </button>
      ))}
    </div>
  );
}

export default function LmsSection({ initialModuleId, initialLessonId }) {
  const [modules, setModules] = useState(null);
  const [active, setActive] = useState(initialModuleId || null);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const searchTimer = React.useRef(null);

  // If initialModuleId changes (from adaptive tutor), open that module
  useEffect(() => {
    if (initialModuleId) setActive(initialModuleId);
  }, [initialModuleId]);

  useEffect(() => {
    if (active) return;
    fetch(`${API}/api/lms/modules`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setModules)
      .catch(() => setModules([]));
  }, [active]);

  const doSearch = useCallback((q) => {
    if (!q || q.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    fetch(`${API}/api/lms/search?q=${encodeURIComponent(q.trim())}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => { setSearchResults(data); setSearching(false); })
      .catch(() => { setSearchResults([]); setSearching(false); });
  }, []);

  const onSearchChange = (e) => {
    const q = e.target.value;
    setSearchQ(q);
    clearTimeout(searchTimer.current);
    if (q.trim().length >= 2) {
      searchTimer.current = setTimeout(() => doSearch(q), 400);
    } else {
      setSearchResults(null);
    }
  };

  const handleSearchSelect = (lesson) => {
    setActive(lesson.course_id);
    setSearchQ("");
    setSearchResults(null);
  };

  if (active) return <ModuleView moduleId={active} onBack={() => setActive(null)} targetLessonId={initialLessonId} />;

  if (!modules)
    return <p className="rp-display rp-italic text-lg text-[#5C5042]">One moment…</p>;

  if (!modules.length)
    return (
      <div className="rp-foam bg-white/45 p-10 text-center" data-testid="lms-empty-state">
        <GraduationCap className="mx-auto mb-4 text-[#B5793F]" size={24} strokeWidth={1.5} />
        <p className="rp-display rp-italic text-base md:text-lg text-[#8A7E6B]">
          Learning modules are being produced — they'll appear here soon.
        </p>
      </div>
    );

  return (
    <div data-testid="lms-section">
      {/* Search bar */}
      <div className="mb-5 relative" data-testid="lms-search-bar">
        <div className="flex items-center gap-2 rp-foam bg-white/70 px-4 py-2.5 rounded-xl">
          <Search size={16} className="text-[#8A7E6B] shrink-0" />
          <input value={searchQ} onChange={onSearchChange}
                 placeholder="Search across all lessons…"
                 className="flex-1 bg-transparent text-sm text-[#262016] placeholder:text-[#8A7E6B] outline-none"
                 data-testid="lms-search-input" />
          {searchQ && (
            <button onClick={() => { setSearchQ(""); setSearchResults(null); }} className="text-[#8A7E6B] hover:text-[#262016]" data-testid="lms-search-clear">
              <X size={14} />
            </button>
          )}
          {searching && <span className="text-[11px] text-[#B5793F] animate-pulse">Searching…</span>}
        </div>
      </div>

      <p className="text-[12px] text-[#8A7E6B] mb-5 leading-relaxed" data-testid="lms-attribution">
        All content is created / assembled by me. Additional resources are sourced from official company channels only.
      </p>
      {searchResults !== null ? (
        <SearchResults results={searchResults} onSelect={handleSearchSelect} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="lms-module-grid">
          {modules.map((c) => <ModuleCard key={c.id} c={c} onOpen={setActive} />)}
        </div>
      )}
    </div>
  );
}
